import { z } from 'zod';

export const SIGNATURE_QUESTION_TYPE_KEY = 'signature';

export const MATCH_ATTENDEE_NAME_RULE = 'matchAttendeeName';

// What the form holds while the attendee signs. `agreed` is the "this is my
// signature" checkbox; `signedAt` is only ever set by the server.
export type SignatureInput = { name: string; agreed?: boolean; signedAt?: string };

export type SignatureAnswer = { name: string; signedAt: string };

export const signatureInputSchema = z.object({
	name: z.string().refine((name) => name.trim().length > 0),
	agreed: z.boolean().optional(),
	signedAt: z.string().optional()
});

function parseJsonString(value: unknown): unknown {
	if (typeof value !== 'string') return value;
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

export function readSignatureAnswer(value: unknown): SignatureInput | null {
	const parsed = signatureInputSchema.safeParse(parseJsonString(value));
	return parsed.success ? parsed.data : null;
}

// Re-saving the same name keeps the moment it was first signed; a new or
// changed name has to come with the agreement and is stamped now.
export function stampSignature(
	input: SignatureInput,
	stored: SignatureAnswer | null,
	now: string
): SignatureAnswer | null {
	const name = input.name.trim().replace(/\s+/g, ' ');
	if (stored && stored.name === name) return { name, signedAt: stored.signedAt };
	if (input.agreed !== true) return null;
	return { name, signedAt: now };
}

function normalizeName(value: string): string {
	return value
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();
}

export function signatureMatchesName(
	signature: string,
	firstName: string | null | undefined,
	lastName: string | null | undefined
): boolean {
	const expected = normalizeName(`${firstName ?? ''} ${lastName ?? ''}`);
	return expected.length > 0 && normalizeName(signature) === expected;
}

type SignatureQuestion = {
	formFieldId: string;
	formFieldValidationRules?: { validationType: string; validationValue: string | null }[] | null;
};

export function signaturesNotMatchingName(
	fields: SignatureQuestion[],
	answers: Record<string, unknown>,
	signer: { firstName: string | null | undefined; lastName: string | null | undefined }
): string[] {
	return fields
		.filter((field) => {
			const mustMatch = field.formFieldValidationRules?.some(
				(rule) => rule.validationType === MATCH_ATTENDEE_NAME_RULE && rule.validationValue === 'true'
			);
			const signature = readSignatureAnswer(answers[field.formFieldId]);
			return mustMatch && signature && !signatureMatchesName(signature.name, signer.firstName, signer.lastName);
		})
		.map((field) => field.formFieldId);
}

export function formatSignedAt(signedAt: string): string {
	return new Date(signedAt).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		timeZoneName: 'short'
	});
}

export function describeSignature(value: unknown): string | null {
	const signature = readSignatureAnswer(value);
	if (!signature) return null;
	return signature.signedAt ? `${signature.name} (signed ${formatSignedAt(signature.signedAt)})` : signature.name;
}
